"use client";

// React context layer for the i18n system. Mirrors the theme system but, because
// translation has to re-render the visible tree on change, it uses a context + a
// cross-component event so EVERY consumer of useT()/useLang() updates the instant
// the flag toggle flips the language — no page reload.
//
// Usage:
//   const { t, lang, setLang } = useLang();   // full API
//   const t = useT();                          // just the translator
//
// The provider is optional: useT() works even outside a provider (it reads
// localStorage + subscribes to the change event directly), so components on pages
// that don't wrap a provider still translate and still re-render on toggle.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getStoredLang, setStoredLang, type Lang } from "@/lib/lang";
import { translations } from "./translations";

const EVENT = "draffte-lang-change";

/** Translate a key for a given language, falling back en → key. */
function translate(lang: Lang, key: string): string {
  return translations[lang]?.[key] ?? translations.en[key] ?? key;
}

type LangContextValue = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => string;
};

const LangContext = createContext<LangContextValue | null>(null);

/** Subscribe a component to the current language; re-renders on every change. */
function useLangState(): Lang {
  const [lang, setLangLocal] = useState<Lang>("en");

  useEffect(() => {
    // Hydrate from storage after mount (SSR-safe), then listen for changes.
    setLangLocal(getStoredLang());
    const onChange = (e: Event) => {
      const next = (e as CustomEvent<Lang>).detail;
      setLangLocal(next ?? getStoredLang());
    };
    window.addEventListener(EVENT, onChange);
    // Cross-tab: storage event fires in OTHER tabs when localStorage changes.
    const onStorage = (e: StorageEvent) => {
      if (e.key === "draffte-lang") setLangLocal(getStoredLang());
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return lang;
}

/** Persist + broadcast a language change so all subscribers re-render now. */
export function changeLang(next: Lang) {
  setStoredLang(next);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent<Lang>(EVENT, { detail: next }));
  }
}

export function LangProvider({ children }: { children: React.ReactNode }) {
  const lang = useLangState();
  const setLang = useCallback((l: Lang) => changeLang(l), []);
  const t = useCallback((key: string) => translate(lang, key), [lang]);
  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

/** Full i18n API. Works inside OR outside a LangProvider. */
export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  // Always call the subscriber hook (rules-of-hooks); only USE it when there's
  // no surrounding provider, so provider-less pages still re-render on toggle.
  const standaloneLang = useLangState();
  if (ctx) return ctx;
  return {
    lang: standaloneLang,
    setLang: changeLang,
    t: (key: string) => translate(standaloneLang, key),
  };
}

/** Convenience: just the translator. */
export function useT(): (key: string) => string {
  return useLang().t;
}
